/// <reference lib="webworker" />
/**
 * Decode side of the background render pipeline.
 *
 * Wraps mediabunny so the worker loop can ask, per output frame, for "the frame
 * of source S at source-time t" and get back a {@link DrawableFrame} the
 * compositor can blit + scale. Video sources are decoded frame-accurately with a
 * {@link VideoSampleSink}; image sources are decoded once via `createImageBitmap`.
 *
 * The decoder is intentionally dumb about the timeline — it only knows sources.
 * The worker maps output time → source time and drives this.
 *
 * Verified against mediabunny v1.49.0 (node_modules/mediabunny/dist/mediabunny.d.ts):
 *   - `new Input({ formats, source })`, `BlobSource`, `UrlSource`
 *   - `input.getPrimaryVideoTrack(): Promise<InputVideoTrack | null>`
 *   - `new VideoSampleSink(track)`, `sink.getSample(t): Promise<VideoSample | null>`
 *   - `VideoSample.draw(ctx, dx, dy, dWidth, dHeight)` DOES scale (4-arg overload),
 *     so it maps straight onto `DrawableFrame.draw(ctx, dx, dy, dw, dh)`.
 *   - async `track.getDisplayWidth()/getDisplayHeight()` (the sync getters are
 *     deprecated and throw if metadata isn't loaded yet).
 */
import {
  Input,
  ALL_FORMATS,
  BlobSource,
  UrlSource,
  VideoSampleSink,
  VideoSample,
  InputVideoTrack,
  InputAudioTrack,
} from 'mediabunny';
import { RenderSource } from './render-job.model';
import { DrawableFrame } from './worker-compositor';

/** A decoded VideoSample wrapped so the compositor can draw+scale it, plus a closer. */
export interface DecodedVisual {
  frame: DrawableFrame;
  /** Release the underlying VideoSample/VideoFrame. Always call after the frame is drawn. */
  close: () => void;
}

/**
 * Sequential frame reader — the speed fix (#1).
 *
 * Instead of `sink.getSample(t)` per output frame (which SEEKS, forcing NVDEC to
 * re-decode from the nearest keyframe every time → ~1× real-time), we stream the
 * source in order with `sink.samples()` and hold the frame that covers the
 * requested time. For monotonically-advancing playback (the normal case) NVDEC
 * decodes straight through at full speed. A backward jump (loop/reverse/another
 * clip on the same source) transparently restarts the stream from that point.
 */
class SequentialFrameReader {
  private iter: AsyncGenerator<VideoSample, void, unknown> | null = null;
  private current: VideoSample | null = null;
  private next: VideoSample | null = null;
  private exhausted = false;

  /**
   * A forward jump larger than this (seconds) is treated as a SEEK, not a playback
   * step. Normal playback / a render's monotonic sweep advances by ~1 frame (≤0.05s),
   * so this never fires there. But a timeline CLICK jumps seconds ahead — streaming
   * and decoding every intermediate frame to reach a distant target is exactly what
   * made scrubbing feel slow (and flash the background while it caught up). Seeking to
   * the nearest keyframe and decoding the few frames up to `t` is far cheaper and
   * returns the identical frame (still the latest sample with timestamp <= t).
   */
  private static readonly SEEK_AHEAD_SECONDS = 1.0;

  constructor(private readonly sink: VideoSampleSink) {}

  /** Returns the sample covering source-time `t` (latest sample with timestamp <= t). */
  async frameAt(t: number): Promise<VideoSample | null> {
    const currentT = this.current?.timestamp ?? Infinity;
    // (Re)seek the stream on the first call, a backward jump (> ~1 frame), or a large
    // forward jump (a scrub/click rather than a playback advance).
    if (
      this.iter === null ||
      t < currentT - 0.04 ||
      t > currentT + SequentialFrameReader.SEEK_AHEAD_SECONDS
    ) {
      await this.restart(t);
    }
    if (!this.current) return null;

    while (!this.exhausted) {
      if (!this.next) {
        const r = await this.iter!.next();
        if (r.done) { this.exhausted = true; break; }
        this.next = r.value;
      }
      if (this.next.timestamp <= t + 0.0001) {
        this.closeSample(this.current);
        this.current = this.next;
        this.next = null;
      } else {
        break;
      }
    }
    return this.current;
  }

  private async restart(fromT: number): Promise<void> {
    this.closeHeld();
    // Return the previous generator before dropping it. mediabunny's samples()
    // generator buffers decoded-ahead VideoSamples internally (its sampleQueue +
    // lastSample); only its return() closes them. Abandoning it leaks those samples
    // → "A VideoSample was garbage collected without first being closed". close()
    // already does this; restart() must too — and it matters now that a far-forward
    // scrub re-seeks (restarts) instead of streaming, so this runs on every click.
    // return() closes the buffered samples synchronously, so we don't await it.
    const previous = this.iter;
    this.iter = null;
    if (previous) {
      try { void previous.return?.(undefined); } catch { /* ignore */ }
    }
    this.exhausted = false;
    this.iter = this.sink.samples(Math.max(0, fromT));
    const r = await this.iter.next();
    if (r.done) { this.exhausted = true; this.current = null; } else { this.current = r.value; }
  }

  private closeSample(s: VideoSample | null): void {
    if (s) { try { s.close(); } catch { /* already closed */ } }
  }
  private closeHeld(): void {
    this.closeSample(this.current);
    this.closeSample(this.next);
    this.current = null;
    this.next = null;
  }
  close(): void {
    this.closeHeld();
    try { void this.iter?.return?.(undefined); } catch { /* ignore */ }
    this.iter = null;
  }
}

/** Per-source decode state held for the whole job. */
export class DecodedSource {
  readonly id: string;
  readonly kind: RenderSource['kind'];

  // video
  private input: Input | null = null;
  private videoTrack: InputVideoTrack | null = null;
  audioTrack: InputAudioTrack | null = null;
  private sink: VideoSampleSink | null = null;
  private reader: SequentialFrameReader | null = null;

  // image
  private bitmap: ImageBitmap | null = null;

  width = 0;
  height = 0;
  durationSeconds = 0;

  private constructor(source: RenderSource) {
    this.id = source.id;
    this.kind = source.kind;
    this.width = source.width ?? 0;
    this.height = source.height ?? 0;
    this.durationSeconds = source.durationSeconds ?? 0;
  }

  /** Builds the decoder for one source (video sink / image bitmap / audio-only input). */
  static async create(source: RenderSource): Promise<DecodedSource> {
    const ds = new DecodedSource(source);

    if (source.kind === 'image') {
      const blob = source.blob ?? (await fetchAsBlob(source.url));
      ds.bitmap = await createImageBitmap(blob);
      ds.width = ds.bitmap.width;
      ds.height = ds.bitmap.height;
      return ds;
    }

    // video or audio → mediabunny Input
    const mediaSource = source.blob
      ? new BlobSource(source.blob)
      : new UrlSource(requireUrl(source.url));
    ds.input = new Input({ formats: ALL_FORMATS, source: mediaSource });

    if (source.kind === 'video') {
      ds.videoTrack = await ds.input.getPrimaryVideoTrack();
      if (ds.videoTrack) {
        ds.sink = new VideoSampleSink(ds.videoTrack);
        try {
          ds.width = await ds.videoTrack.getDisplayWidth();
          ds.height = await ds.videoTrack.getDisplayHeight();
        } catch {
          /* fall back to source hints already set */
        }
      }
    }

    // Both video and audio sources may carry an audio track (used by the mixer).
    try {
      ds.audioTrack = await ds.input.getPrimaryAudioTrack();
    } catch {
      ds.audioTrack = null;
    }

    if (!ds.durationSeconds && ds.input) {
      try {
        ds.durationSeconds = await ds.input.computeDuration();
      } catch {
        /* leave 0; clip windows still drive the render */
      }
    }

    return ds;
  }

  hasVideo(): boolean {
    return !!this.sink;
  }

  /**
   * Returns a drawable for this source at the given SOURCE-time (seconds).
   * For video: frame-accurate sample (caller MUST close it after drawing).
   * For image: the shared ImageBitmap (no per-frame allocation; close() is a no-op).
   */
  async getDrawable(sourceTimeSeconds: number): Promise<DecodedVisual | null> {
    if (this.kind === 'image') {
      if (!this.bitmap) return null;
      const bmp = this.bitmap;
      return {
        frame: {
          width: bmp.width,
          height: bmp.height,
          textureSource: bmp,
          draw: (ctx, dx, dy, dw, dh) => ctx.drawImage(bmp, dx, dy, dw, dh),
        },
        close: () => {
          /* shared bitmap stays alive for the whole job */
        },
      };
    }

    if (!this.sink) return null;
    const t = Math.max(0, sourceTimeSeconds);

    // Fast path: stream the source forward (#1). The reader owns the sample's
    // lifecycle (it's reused across output frames until the stream advances),
    // so the returned drawable's close() is a no-op here.
    if (!this.reader) this.reader = new SequentialFrameReader(this.sink);
    let sample: VideoSample | null = null;
    let readerOwned = true;
    try {
      sample = await this.reader.frameAt(t);
    } catch {
      sample = null;
    }
    // Fallback: one-off seek if streaming failed (caller closes this one).
    if (!sample) {
      readerOwned = false;
      try {
        sample = await this.sink.getSample(t);
      } catch {
        sample = null;
      }
    }
    if (!sample) return null;

    const drawn = sample;
    const w = drawn.displayWidth || this.width || drawn.codedWidth;
    const h = drawn.displayHeight || this.height || drawn.codedHeight;
    return {
      frame: {
        width: w,
        height: h,
        // 4-arg draw overload scales the sample into the dest rect.
        draw: (ctx, dx, dy, dw, dh) => drawn.draw(ctx, dx, dy, dw, dh),
      },
      close: () => {
        if (readerOwned) return; // reader closes it on advance / dispose
        try {
          drawn.close();
        } catch {
          /* already closed */
        }
      },
    };
  }

  /**
   * Returns a CALLER-OWNED drawable for this source at the given SOURCE-time.
   *
   * Unlike {@link getDrawable} (which streams via the shared SequentialFrameReader
   * and HANDS BACK a frame whose lifecycle the reader owns), this does a one-off
   * `sink.getSample(t)` seek and returns a fresh VideoSample that the caller must
   * close — `close()` here really closes. This is what lets TWO clips that share a
   * `sourceId` draw at the same output time: the streaming reader can only hold one
   * sample at a time (advancing it for clip #2 closes clip #1's frame), so the
   * SECOND-and-later same-source clip of a frame must own its own sample.
   *
   * Images return the shared bitmap exactly like {@link getDrawable} (no per-frame
   * sample to own; close() is a real no-op), so an image used by two clips is safe
   * either way.
   */
  async getOwnedDrawable(sourceTimeSeconds: number): Promise<DecodedVisual | null> {
    if (this.kind === 'image') {
      // Images have no streaming reader; the shared bitmap is fine for any number
      // of simultaneous clips. Reuse the identical drawable.
      return this.getDrawable(sourceTimeSeconds);
    }

    if (!this.sink) return null;
    const t = Math.max(0, sourceTimeSeconds);

    let sample: VideoSample | null = null;
    try {
      sample = await this.sink.getSample(t);
    } catch {
      sample = null;
    }
    if (!sample) return null;

    const drawn = sample;
    const w = drawn.displayWidth || this.width || drawn.codedWidth;
    const h = drawn.displayHeight || this.height || drawn.codedHeight;
    return {
      frame: {
        width: w,
        height: h,
        draw: (ctx, dx, dy, dw, dh) => drawn.draw(ctx, dx, dy, dw, dh),
      },
      // Caller-owned: this really closes the sample (no reader sharing it).
      close: () => {
        try {
          drawn.close();
        } catch {
          /* already closed */
        }
      },
    };
  }

  /** Releases the shared image bitmap. Video samples are closed per-frame by callers. */
  dispose(): void {
    if (this.bitmap) {
      try {
        this.bitmap.close();
      } catch {
        /* ignore */
      }
      this.bitmap = null;
    }
    // mediabunny Input has no explicit close in the public API beyond GC; drop refs.
    this.reader?.close();
    this.reader = null;
    this.sink = null;
    this.videoTrack = null;
    this.input = null;
  }
}

/** Builds a decoder for every source, keyed by source id. Failures map to null entries. */
export async function buildDecodedSources(
  sources: RenderSource[],
  onError?: (sourceId: string, err: unknown) => void,
): Promise<Map<string, DecodedSource>> {
  const map = new Map<string, DecodedSource>();
  await Promise.all(
    sources.map(async src => {
      try {
        map.set(src.id, await DecodedSource.create(src));
      } catch (err) {
        onError?.(src.id, err);
      }
    }),
  );
  return map;
}

async function fetchAsBlob(url: string | null | undefined): Promise<Blob> {
  const res = await fetch(requireUrl(url));
  if (!res.ok) throw new Error(`Failed to fetch source (${res.status}) ${url}`);
  return res.blob();
}

function requireUrl(url: string | null | undefined): string {
  if (!url) throw new Error('RenderSource has neither blob nor url.');
  return url;
}
